import { useState, useCallback, useEffect } from 'react';
import { useFetchWeeklyGLPostingFeedsMutation } from '../../../services/WeeklyGLPosting/WeeklyGLPostingService';
import { APPROVED_POSTING_FALLBACK_FEED_SOURCE_DATA, DEFAULTS } from '../constants';
import {
  buildWeeklyGLErrorMessage,
  extractFeedSourceData,
  extractSublocationBreakdown,
  extractWarnings,
  shouldShowWeeklyGLLocalErrorModal,
  toTrimmedString,
} from '../utils/glPostingMethods';

const extractResyncData = (payload) => {
  const resync = payload?.data?.resync ?? payload?.resync ?? payload?.data?.posting?.resync ?? payload?.posting?.resync;

  if (!resync) return DEFAULTS.EMPTY_ARRAY;
  const values = Array.isArray(resync) ? resync : Object.values(resync);

  return values.reduce((acc, item) => {
    if (Array.isArray(item)) {
      acc.push(...extractResyncData({ resync: item }));
    } else if (item) {
      acc.push(item);
    }

    return acc;
  }, []);
};

export const useGLPostingDataPanel = ({
  postingDetails,
  isApprovedPosting,
  normalizedPostingId,
  persistedWarnings,
  persistedFeedSourceData,
  persistedIsApplyingFilters,
}) => {
  const [warnings, setWarnings] = useState(persistedWarnings);
  const [warningCount, setWarningCount] = useState(persistedWarnings.length);
  const [feedSourceData, setFeedSourceData] = useState(persistedFeedSourceData);
  const [sublocationBreakdown, setSublocationBreakdown] = useState(DEFAULTS.EMPTY_ARRAY);
  const [resyncData, setResyncData] = useState(DEFAULTS.EMPTY_ARRAY);
  const [isApplyingFilters, setIsApplyingFilters] = useState(persistedIsApplyingFilters);
  const [isFetchingFeeds, setIsFetchingFeeds] = useState(false);
  const [showApiErrorModal, setShowApiErrorModal] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState(DEFAULTS.EMPTY_STRING);
  const [fetchWeeklyGLPostingFeeds] = useFetchWeeklyGLPostingFeedsMutation();

  useEffect(() => {
    if (!postingDetails) {
      if (normalizedPostingId || isApprovedPosting) {
        setWarnings(DEFAULTS.EMPTY_ARRAY);
        setWarningCount(DEFAULTS.ZERO);
        setFeedSourceData(DEFAULTS.EMPTY_ARRAY);
        setSublocationBreakdown(DEFAULTS.EMPTY_ARRAY);
        setResyncData(DEFAULTS.EMPTY_ARRAY);
      }
      return;
    }

    const nextWarnings = extractWarnings(postingDetails);
    setWarnings(nextWarnings);
    setWarningCount(Array.isArray(nextWarnings) ? nextWarnings.length : 0);

    const nextFeedSourceData = extractFeedSourceData(postingDetails);
    setResyncData(extractResyncData(postingDetails));
    // Keep zeroed rows visible in approved details until summaries are consistently populated by backend.
    if (isApprovedPosting && (!Array.isArray(nextFeedSourceData) || nextFeedSourceData.length === 0)) {
      setFeedSourceData(APPROVED_POSTING_FALLBACK_FEED_SOURCE_DATA);
      setSublocationBreakdown(DEFAULTS.EMPTY_ARRAY);
      return;
    }

    setFeedSourceData(nextFeedSourceData);
    setSublocationBreakdown(extractSublocationBreakdown(postingDetails));
  }, [postingDetails, isApprovedPosting, normalizedPostingId]);

  const handleApiError = useCallback((error) => {
    if (!shouldShowWeeklyGLLocalErrorModal(error)) return;
    const message = buildWeeklyGLErrorMessage(error);
    if (!message) return;
    setApiErrorMessage(message);
    setShowApiErrorModal(true);
  }, []);

  const handleCloseApiErrorModal = useCallback(() => {
    setShowApiErrorModal(false);
    setApiErrorMessage(DEFAULTS.EMPTY_STRING);
  }, []);

  const handleApplyFilters = useCallback(
    async (applyPayload = {}) => {
      const selectedX3ProductName = toTrimmedString(applyPayload?.x3ProductName);
      const selectedCurrencyIds = Array.isArray(applyPayload?.currencyIds) ? applyPayload.currencyIds : [];
      const selectedStartDate = toTrimmedString(applyPayload?.startDate);
      const selectedEndDate = toTrimmedString(applyPayload?.endDate);
      const selectedIds = Array.isArray(applyPayload?.sublocationIds) ? applyPayload.sublocationIds : [];
      const selectedSublocationDetails = Array.isArray(applyPayload?.selectedSublocationDetails)
        ? applyPayload.selectedSublocationDetails
        : DEFAULTS.EMPTY_ARRAY;
      const currencyIds = selectedCurrencyIds
        .map((idValue) => Number(idValue))
        .filter((idValue) => Number.isInteger(idValue) && idValue > 0);
      const sublocationIds = selectedIds
        .map((idValue) => Number(idValue))
        .filter((idValue) => Number.isInteger(idValue) && idValue > 0);

      if (
        !selectedX3ProductName ||
        currencyIds.length === 0 ||
        !selectedStartDate ||
        !selectedEndDate ||
        sublocationIds.length === 0
      ) {
        return;
      }

      setWarnings(DEFAULTS.EMPTY_ARRAY);
      setWarningCount(DEFAULTS.ZERO);
      setFeedSourceData(DEFAULTS.EMPTY_ARRAY);
      setSublocationBreakdown(DEFAULTS.EMPTY_ARRAY);
      setResyncData(DEFAULTS.EMPTY_ARRAY);
      setIsApplyingFilters(false);
      setIsFetchingFeeds(true);
      setShowApiErrorModal(false);
      setApiErrorMessage(DEFAULTS.EMPTY_STRING);

      try {
        const response = await fetchWeeklyGLPostingFeeds({
          x3ProductName: selectedX3ProductName,
          sublocationIds,
          startDate: selectedStartDate,
          endDate: selectedEndDate,
        }).unwrap();

        const nextFeedSourceData = extractFeedSourceData(response);
        const nextResyncData = extractResyncData(response);
        const sublocationNamesMap = selectedSublocationDetails.reduce((acc, sublocation) => {
          const id = String(sublocation?.id ?? '').trim();
          const name = toTrimmedString(sublocation?.name);
          if (id && name) {
            acc[id] = name;
          }
          return acc;
        }, {});
        const nextWarnings = extractWarnings(response, { sublocationNameById: sublocationNamesMap });

        setFeedSourceData(nextFeedSourceData);
        setSublocationBreakdown(extractSublocationBreakdown(response));
        setResyncData(nextResyncData);
        setWarnings(nextWarnings);
        setWarningCount(nextWarnings.length);
        setIsApplyingFilters(true);
      } catch (error) {
        handleApiError(error);
        setWarnings(DEFAULTS.EMPTY_ARRAY);
        setWarningCount(DEFAULTS.ZERO);
        setFeedSourceData(DEFAULTS.EMPTY_ARRAY);
        setSublocationBreakdown(DEFAULTS.EMPTY_ARRAY);
        setResyncData(DEFAULTS.EMPTY_ARRAY);
        setIsApplyingFilters(false);
      } finally {
        setIsFetchingFeeds(false);
      }
    },
    [fetchWeeklyGLPostingFeeds, handleApiError],
  );

  const resetDataPanelState = useCallback(() => {
    setWarnings(DEFAULTS.EMPTY_ARRAY);
    setWarningCount(DEFAULTS.ZERO);
    setFeedSourceData(DEFAULTS.EMPTY_ARRAY);
    setSublocationBreakdown(DEFAULTS.EMPTY_ARRAY);
    setResyncData(DEFAULTS.EMPTY_ARRAY);
    setIsApplyingFilters(false);
    setIsFetchingFeeds(false);
  }, []);

  return {
    warnings,
    warningCount,
    feedSourceData,
    sublocationBreakdown,
    resyncData,
    isApplyingFilters,
    isFetchingFeeds,
    showApiErrorModal,
    apiErrorMessage,
    handleApiError,
    handleCloseApiErrorModal,
    handleApplyFilters,
    resetDataPanelState,
  };
};
